import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Ban, Search, Plus, Trash2, Upload, Download, RefreshCw, 
  Phone, Calendar, AlertTriangle, CheckCircle, Filter, X,
  MessageSquare, Smartphone, FileSpreadsheet, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sourceLabels = {
  rcs_google: { label: 'RCS Google', color: 'bg-blue-500' },
  sms_stop: { label: 'SMS STOP', color: 'bg-amber-500' },
  manual: { label: 'Manuel', color: 'bg-slate-500' },
  import: { label: 'Import', color: 'bg-purple-500' },
  api: { label: 'API', color: 'bg-green-500' }
};

const reasonLabels = {
  not_signed_up: 'Non inscrit',
  too_many_messages: 'Trop de messages',
  no_longer_interested: 'Plus intéressé',
  spam: 'Spam',
  other: 'Autre',
  unknown: 'Non spécifié'
};

export default function Blacklist() {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [searchPhone, setSearchPhone] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newSource, setNewSource] = useState('manual');
  const [newReason, setNewReason] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [bulkPhones, setBulkPhones] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      
      if (searchPhone) params.append('search', searchPhone);
      if (filterSource !== 'all') params.append('source', filterSource);
      
      const response = await fetch(`${API_URL}/api/v2/blacklist?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token, page, searchPhone, filterSource]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/blacklist/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries, fetchStats]);

  const handleAddEntry = async () => {
    if (!newPhone.trim()) {
      toast.error('Numéro de téléphone requis');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/v2/blacklist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: newPhone,
          source: newSource,
          reason: newReason || null,
          notes: newNotes || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Numéro ajouté à la blacklist');
        setAddModalOpen(false);
        setNewPhone('');
        setNewNotes('');
        fetchEntries();
        fetchStats();
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleBulkAdd = async () => {
    const phones = bulkPhones.split('\n').filter(p => p.trim());
    
    if (phones.length === 0) {
      toast.error('Aucun numéro à importer');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/v2/blacklist/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_numbers: phones,
          source: 'import'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.added_count} numéros ajoutés, ${data.skipped_count} ignorés`);
        setBulkModalOpen(false);
        setBulkPhones('');
        fetchEntries();
        fetchStats();
      }
    } catch (err) {
      toast.error('Erreur lors de l\'import');
    }
  };

  const handleRemove = async (phone) => {
    if (!window.confirm(`Retirer ${phone} de la blacklist ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/v2/blacklist/${encodeURIComponent(phone)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Numéro retiré de la blacklist');
        fetchEntries();
        fetchStats();
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="blacklist-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              <Ban className="inline-block w-7 h-7 mr-2 text-red-500" />
              Blacklist / Désabonnements
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Gestion des numéros STOP (RCS et SMS)
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBulkModalOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Import en masse
            </Button>
            <Button onClick={() => setAddModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un numéro
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total blacklist</p>
                    <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stats.total}</p>
                  </div>
                  <Ban className="w-10 h-10 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>RCS Google</p>
                    <p className={`text-3xl font-bold text-blue-500`}>{stats.by_source?.rcs_google || 0}</p>
                  </div>
                  <Smartphone className="w-10 h-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>SMS STOP</p>
                    <p className={`text-3xl font-bold text-amber-500`}>{stats.by_source?.sms_stop || 0}</p>
                  </div>
                  <MessageSquare className="w-10 h-10 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Globaux</p>
                    <p className={`text-3xl font-bold text-purple-500`}>{stats.global_count || 0}</p>
                  </div>
                  <AlertTriangle className="w-10 h-10 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label>Rechercher un numéro</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    placeholder="+33612345678"
                    className="pl-10"
                    data-testid="search-phone-input"
                  />
                </div>
              </div>
              
              <div className="w-48">
                <Label>Source</Label>
                <Select value={filterSource} onValueChange={setFilterSource}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les sources</SelectItem>
                    <SelectItem value="rcs_google">RCS Google</SelectItem>
                    <SelectItem value="sms_stop">SMS STOP</SelectItem>
                    <SelectItem value="manual">Manuel</SelectItem>
                    <SelectItem value="import">Import</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="outline" onClick={() => { setSearchPhone(''); setFilterSource('all'); }}>
                <X className="w-4 h-4 mr-2" />
                Réinitialiser
              </Button>
              
              <Button variant="outline" onClick={fetchEntries}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
          <CardHeader>
            <CardTitle className={isDarkMode ? 'text-white' : ''}>
              Numéros blacklistés ({total})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-8">
                <Ban className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                  Aucun numéro dans la blacklist
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Numéro</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Source</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Raison</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Date</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr 
                        key={entry.id} 
                        className={`border-b ${isDarkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-100 hover:bg-slate-50'}`}
                      >
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-white' : ''}`}>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="font-mono">{entry.phone_number}</span>
                            {entry.is_global && (
                              <Badge variant="outline" className="text-xs">Global</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${sourceLabels[entry.source]?.color || 'bg-slate-500'} text-white`}>
                            {sourceLabels[entry.source]?.label || entry.source}
                          </Badge>
                        </td>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                          {reasonLabels[entry.reason] || '-'}
                        </td>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {new Date(entry.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemove(entry.phone_number)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  Page {page} sur {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Modal */}
        <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter à la blacklist</DialogTitle>
              <DialogDescription>
                Ajouter manuellement un numéro à la liste des désabonnements
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Numéro de téléphone *</Label>
                <Input
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+33612345678"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={newSource} onValueChange={setNewSource}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manuel</SelectItem>
                    <SelectItem value="sms_stop">SMS STOP reçu</SelectItem>
                    <SelectItem value="api">API externe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Raison (optionnel)</Label>
                <Select value={newReason} onValueChange={setNewReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_signed_up">Non inscrit</SelectItem>
                    <SelectItem value="too_many_messages">Trop de messages</SelectItem>
                    <SelectItem value="no_longer_interested">Plus intéressé</SelectItem>
                    <SelectItem value="spam">Spam</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Contexte, demande client..."
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddEntry}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Modal */}
        <Dialog open={bulkModalOpen} onOpenChange={setBulkModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Import en masse</DialogTitle>
              <DialogDescription>
                Importez plusieurs numéros à la fois (un par ligne)
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <Textarea
                value={bulkPhones}
                onChange={(e) => setBulkPhones(e.target.value)}
                placeholder={"+33612345678\n+33698765432\n+33611223344"}
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-sm text-slate-500">
                {bulkPhones.split('\n').filter(p => p.trim()).length} numéro(s) détecté(s)
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleBulkAdd}>
                <Upload className="w-4 h-4 mr-2" />
                Importer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
